import { ref } from 'vue';
import { defineStore } from 'pinia';
import { v4 as uuid } from 'uuid';
import { recordSurveyEvent } from '../api/job-quality-survey.js';

const VIEW_THRESHOLD = 3;
const SCROLL_CARD_INDEX = 14; // 0-based index of the 15th job card
const AUTO_DISMISS_MS = 1500;

// Persists the engagement counters + once-per-cycle latch for the browser session, so the
// prompt is not re-armed by SPA navigation or a hard refresh. Cleared only on AutoApply/Apply.
const CYCLE_STORAGE_KEY = 'jobQualitySurveyCycle';

/**
 * Reasons shown as single-select chips. `key` is persisted for analytics; `label` is rendered.
 */
export const SURVEY_REASONS = [
	{ key: 'wrong_location', label: 'Wrong location' },
	{ key: 'wrong_seniority', label: 'Wrong seniority level' },
	{ key: 'salary_below', label: 'Salary below expectations' },
	{ key: 'wrong_industry_role', label: 'Wrong industry/role' },
	{ key: 'other', label: 'Other' },
];

/**
 * The 0-based card index a user must scroll past to trigger the prompt.
 * 30+ jobs -> the 15th card (index 14); fewer -> past 50% of the list.
 */
export const thresholdIndex = (totalJobs) => {
	if (!totalJobs || totalJobs <= 0) {
		return SCROLL_CARD_INDEX;
	}

	return totalJobs >= 30 ? SCROLL_CARD_INDEX : Math.ceil(totalJobs * 0.5) - 1;
};

export const useJobQualitySurveyStore = defineStore('job-quality-survey', () => {
	// Trigger counters + once-per-cycle latch (all reset on AutoApply/Apply click)
	const viewedJobIds = ref(new Set());
	const maxScrollIndex = ref(-1);
	const shownForThisCycle = ref(false);

	// Best-effort sessionStorage persistence of the cycle state (see CYCLE_STORAGE_KEY).
	const persistCycle = () => {
		try {
			sessionStorage.setItem(
				CYCLE_STORAGE_KEY,
				JSON.stringify({
					viewedJobIds: Array.from(viewedJobIds.value),
					maxScrollIndex: maxScrollIndex.value,
					shownForThisCycle: shownForThisCycle.value,
				}),
			);
		} catch {
			// sessionStorage may be unavailable (private mode / quota) — persistence is best-effort
		}
	};

	const restoreCycle = () => {
		try {
			const stored = sessionStorage.getItem(CYCLE_STORAGE_KEY);
			if (!stored) {
				return;
			}

			const data = JSON.parse(stored);
			viewedJobIds.value = new Set(data.viewedJobIds ?? []);
			maxScrollIndex.value = data.maxScrollIndex ?? -1;
			shownForThisCycle.value = data.shownForThisCycle ?? false;
		} catch {
			// ignore malformed/unavailable storage
		}
	};

	restoreCycle();

	// Prompt UI state
	const visible = ref(false);
	const uiState = ref('default'); // 'default' | 'thanks' | 'other'
	const otherText = ref('');

	// Analytics correlation
	const surveySessionId = ref(null);
	const triggerReason = ref(null); // 'views' | 'scroll'

	let autoDismissTimer = null;

	const clearAutoDismissTimer = () => {
		if (autoDismissTimer) {
			clearTimeout(autoDismissTimer);
			autoDismissTimer = null;
		}
	};

	const buildMeta = (ctx = {}) => ({
		query_id: ctx.queryId ?? null,
		tab: ctx.tab ?? null,
		distinct_views: viewedJobIds.value.size,
		max_scroll_index: maxScrollIndex.value,
		trigger: triggerReason.value,
	});

	const track = (event, ctx = {}, extra = {}) => {
		recordSurveyEvent({
			survey_session_id: surveySessionId.value,
			event,
			meta: buildMeta(ctx),
			...extra,
		});
	};

	const thresholdMet = (totalJobs) =>
		viewedJobIds.value.size >= VIEW_THRESHOLD || maxScrollIndex.value >= thresholdIndex(totalJobs);

	const show = (ctx = {}) => {
		visible.value = true;
		shownForThisCycle.value = true;
		uiState.value = 'default';
		otherText.value = '';
		surveySessionId.value = uuid();
		triggerReason.value = viewedJobIds.value.size >= VIEW_THRESHOLD ? 'views' : 'scroll';
		track('shown', ctx);
	};

	const maybeShow = (ctx = {}) => {
		if (shownForThisCycle.value) {
			return;
		}

		if (thresholdMet(ctx.totalJobs)) {
			show(ctx);
		}
	};

	const recordJobViewed = (providerJobId, ctx = {}) => {
		if (!providerJobId) {
			return;
		}

		viewedJobIds.value.add(providerJobId);
		maybeShow(ctx);
		persistCycle();
	};

	const recordScrollIndex = (index, ctx = {}) => {
		if (index > maxScrollIndex.value) {
			maxScrollIndex.value = index;
		}

		maybeShow(ctx);
		persistCycle();
	};

	const closeImmediate = () => {
		clearAutoDismissTimer();
		visible.value = false;
		uiState.value = 'default';
		otherText.value = '';
	};

	/**
	 * Called on every AutoApply/Apply click: clears counters + latch and closes the prompt,
	 * so the user leaves the "engaged but didn't act" segment and the prompt can fire again later.
	 */
	const resetCounters = () => {
		viewedJobIds.value = new Set();
		maxScrollIndex.value = -1;
		shownForThisCycle.value = false;
		triggerReason.value = null;

		try {
			sessionStorage.removeItem(CYCLE_STORAGE_KEY);
		} catch {
			// best-effort
		}

		closeImmediate();
	};

	const goThanks = () => {
		uiState.value = 'thanks';
		clearAutoDismissTimer();
		autoDismissTimer = setTimeout(closeImmediate, AUTO_DISMISS_MS);
	};

	const selectChip = (reasonKey, ctx = {}) => {
		if (reasonKey === 'other') {
			uiState.value = 'other';
			return;
		}

		track('answered', ctx, { reason: reasonKey });
		goThanks();
	};

	const submitOther = (ctx = {}) => {
		const text = otherText.value.trim();
		if (text === '') {
			return;
		}

		track('other_submitted', ctx, { reason: 'other', other_text: text });
		goThanks();
	};

	const dismiss = (ctx = {}) => {
		track('dismissed', ctx);
		closeImmediate();
	};

	return {
		viewedJobIds,
		maxScrollIndex,
		shownForThisCycle,
		visible,
		uiState,
		otherText,

		recordJobViewed,
		recordScrollIndex,
		resetCounters,
		selectChip,
		submitOther,
		dismiss,
	};
});
